import { Check, ClipboardCopy, RotateCcw, TextCursorInput, X } from 'lucide-react';
import { stripCounselPanelLayoutIndents } from '../../lib/formatCounselPanelHtml';
import { sanitizeAiHtml } from '../../lib/sanitizeHtml';
import { goToPendingDraftChange, type PendingDraftChange } from '../../store/lexCounselStore';

interface DraftChangePreviewProps {
  change: PendingDraftChange;
  onApply: () => void;
  onDiscard: () => void;
  onInsertAtCursor?: () => void;
  onRetry?: () => void;
  disabled?: boolean;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export default function DraftChangePreview({
  change,
  onApply,
  onDiscard,
  onInsertAtCursor,
  onRetry,
  disabled = false,
}: DraftChangePreviewProps) {
  const isDocument = change.scope === 'document';
  const isInsert = change.scope === 'insert';
  const isNewTab = change.scope === 'new_tab';
  const isSelection = change.scope === 'selection';
  const showSummaryOnly = isDocument || isNewTab;
  const originalText = stripHtml(change.originalHtml);
  const proposedText = stripHtml(change.proposedHtml);
  const isHighImpact = change.risk === 'high';
  const keyPoints = change.keyPoints?.filter(Boolean) ?? [];

  const handleCopy = async () => {
    const html = change.proposedHtml.trim();
    const plain = proposedText;
    try {
      if (typeof ClipboardItem !== 'undefined' && html.includes('<')) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plain], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
    } catch {
      await navigator.clipboard.writeText(plain);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-[var(--counsel-border)] bg-[var(--counsel-draft-preview-bg)] overflow-hidden">
      {showSummaryOnly ? (
        <div className="px-3 py-2.5">
          {change.reason ? (
            <p className="text-[12px] leading-relaxed text-[var(--counsel-text-body)]">
              {change.reason}
            </p>
          ) : null}
          {keyPoints[0] ? (
            <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--counsel-text-secondary)]">
              {keyPoints[0]}
            </p>
          ) : null}
          {isHighImpact ? (
            <p className="mt-1.5 text-[11px] leading-relaxed text-amber-700">
              Verify the changed legal detail before applying.
            </p>
          ) : null}
        </div>
      ) : (
        <>
          {change.reason ? (
            <div className="border-b border-[var(--counsel-border-subtle)] px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--counsel-text-muted)]">
                  Why this change
                </p>
                {isHighImpact ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                    Legal detail
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-[12px] leading-relaxed text-[var(--counsel-text-secondary)]">
                {change.reason}
              </p>
              {isHighImpact ? (
                <p className="mt-1.5 text-[11px] leading-relaxed text-amber-700">
                  Review this legal detail carefully before applying it.
                </p>
              ) : null}
            </div>
          ) : null}

          {isSelection ? (
            <button
              type="button"
              onClick={() => goToPendingDraftChange()}
              disabled={disabled}
              className="w-full border-b border-[var(--counsel-border-subtle)] px-3 py-2 text-left transition-colors hover:bg-[var(--counsel-hover-subtle)] disabled:opacity-50"
            >
              <p className="text-[11px] uppercase tracking-wide text-[var(--counsel-accent)]">
                Selection suggestion
              </p>
              <p className="mt-1 text-[12px] leading-relaxed text-[var(--counsel-text-secondary)]">
                The suggested edit is shown inline in your document. Accept or reject it there, or use
                the controls below.
              </p>
              <p className="mt-1.5 text-[11px] font-medium text-[var(--counsel-accent)]">
                Click to jump to the suggestion in your document
              </p>
            </button>
          ) : change.scope === 'paragraph' ? (
            <button
              type="button"
              onClick={() => goToPendingDraftChange()}
              disabled={disabled}
              className="w-full border-b border-[var(--counsel-border-subtle)] px-3 py-2 text-left transition-colors hover:bg-[var(--counsel-hover-subtle)] disabled:opacity-50"
            >
              <p className="text-[11px] uppercase tracking-wide text-[var(--counsel-accent)]">
                Proposed change
              </p>
              <p className="mt-1.5 text-[11px] font-medium text-[var(--counsel-accent)]">
                Click to jump to the paragraph in your document
              </p>
            </button>
          ) : (
            <div className="px-3 py-2 border-b border-[var(--counsel-border-subtle)] text-[11px] uppercase tracking-wide text-[var(--counsel-text-muted)]">
              {isInsert ? 'Generated draft' : 'Proposed change'}
            </div>
          )}
        </>
      )}

      {change.scopeFallbackNotice && (
        <p className="px-3 py-2 text-[12px] leading-relaxed text-[#e37400] border-b border-[var(--counsel-border-subtle)]">
          {change.scopeFallbackNotice}
        </p>
      )}

      {!isSelection && !showSummaryOnly && (
        <div className="grid gap-0">
          {!isInsert && (
            <div className="px-3 py-2 border-b border-[var(--counsel-border-subtle)]">
              <p className="text-[11px] font-medium text-[#d93025] mb-1">Before</p>
              <p className="text-[12px] leading-relaxed text-[var(--counsel-text-secondary)] line-through decoration-[#d93025]/70">
                {originalText || 'Empty paragraph'}
              </p>
            </div>
          )}
          <div className="px-3 py-2">
            {!isInsert && <p className="text-[11px] font-medium text-[#188038] mb-1">After</p>}
            {change.proposedHtml.includes('<') ? (
              <div
                className={`text-[12px] leading-relaxed text-[var(--counsel-text-body)] lexcounsel-content ${isInsert ? 'max-h-48 overflow-y-auto' : ''}`}
                dangerouslySetInnerHTML={{
                  __html: stripCounselPanelLayoutIndents(sanitizeAiHtml(change.proposedHtml)),
                }}
              />
            ) : (
              <p className="text-[12px] leading-relaxed text-[var(--counsel-text-body)]">
                {proposedText || 'No content generated'}
              </p>
            )}
          </div>
        </div>
      )}

      {!change.applied && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-[var(--counsel-border-subtle)]">
          {isInsert ? (
            <>
              <button
                type="button"
                onClick={() => void handleCopy()}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--counsel-chip-bg)] px-3 py-1.5 text-[12px] font-medium text-[var(--counsel-text)] hover:bg-[var(--counsel-hover)] disabled:opacity-50"
              >
                <ClipboardCopy className="h-3.5 w-3.5" />
                Copy
              </button>
              <button
                type="button"
                onClick={onInsertAtCursor}
                disabled={disabled || !onInsertAtCursor}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#188038] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[#137333] disabled:opacity-50"
              >
                <TextCursorInput className="h-3.5 w-3.5" />
                Insert at cursor
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onApply}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#188038] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[#137333] disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              {isNewTab ? 'Create document' : 'Apply'}
            </button>
          )}
          <button
            type="button"
            onClick={onDiscard}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium text-[var(--counsel-text-muted)] hover:bg-[var(--counsel-hover)] hover:text-[var(--counsel-text)] disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
            Discard
          </button>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={disabled}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium text-[var(--counsel-text-muted)] hover:bg-[var(--counsel-hover)] hover:text-[var(--counsel-text)] disabled:opacity-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Retry
            </button>
          )}
        </div>
      )}
      {change.applied && (
        <div className="px-3 py-2 border-t border-[var(--counsel-border-subtle)] text-[12px] text-[#188038]">
          {isInsert ? 'Inserted at cursor.' : 'Applied to document.'}
        </div>
      )}
    </div>
  );
}
